<template>
    <div class="lini-view-container">
        <!-- Ultra-Lean Header Strip (Max 60px) -->
        <div class="lean-header-strip" v-if="document">
            <div class="header-left">
                <div class="doc-identity">
                    <i class="dx-icon-doc"></i>
                    <div class="doc-text">
                        <span class="partner-name truncate" :title="document.Partener">{{ document.Partener }}</span>
                        <div class="doc-meta-chips">
                            <span class="chip no">#{{ document.Numar }}</span>
                            <span class="chip date">{{ formatDate(document.Data) }}</span>
                            <span class="chip type">{{ document.Tip }}</span>

                            <!-- Financial Status Chips -->
                            <span class="chip valoare" v-if="document.Valoare">V: {{
                                document.Valoare.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }}</span>
                            <span class="chip tva" v-if="document.Tva">TVA: {{ document.Tva }}%</span>
                            <span class="chip tva-val" v-if="document.ValoareTva">VTVA: {{
                                document.ValoareTva.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }}</span>
                            <span class="chip total" v-if="document.Total">TOTAL: {{
                                document.Total.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }} {{
                                    document.Valuta
                                }}</span>
                            <span class="chip sold" v-if="document.Sold">SOLD: {{ document.Sold.toLocaleString('ro-RO',
                                { minimumFractionDigits: 2 }) }}</span>
                            <span class="chip curs" v-if="document.Curs">CURS: {{ document.Curs.toLocaleString('ro-RO',
                                { minimumFractionDigits: 4 }) }}</span>
                            <span class="chip adaos" v-if="document.Adaos">ADAOS: {{
                                document.Adaos.toLocaleString('ro-RO', { minimumFractionDigits: 2 }) }}</span>
                        </div>
                    </div>
                </div>
            </div>



            <div class="header-right">
                <DxButton icon="info" hint="Detalii Document" styling-mode="text" @click="$emit('toggle-details')"
                    class="detail-btn" />

                <DxButton icon="share" hint="Partajează" styling-mode="text" @click="shareClicked" />
            </div>
        </div>

        <div v-if="areLinii" class="grid-section">
            <DxDataGrid ref="dataGrid" :data-source="dsLinii" :allowColumnReordering='true' :allowColumnResizing='true'
                :columnAutoWidth='false' :columnMinWidth='50' :show-borders='true' :syncLookupFilterValues='false'
                :filter-sync-enabled='true' @saved="onGridSaved" @saving="onSavingLinie" @editing-start="onEditingStartLinie">
                <DxExport :enabled='true' :formats='["pdf", "xlsx"]' :allow-export-selected-data='true' />
                <DxFilterRow :visible='true' />
                <DxGrouping :context-menu-enabled='true'>
                    <DxTexts group-continued-message='Continuat de pe pagina anterioara'
                        group-continues-message='Continuat pe pagina urmatoare' />
                </DxGrouping>
                <DxEditing :allowUpdating="true" :allowDeleting="false" mode="popup">
                    <DxPopup :closeOnOutsideClick="false" :showCloseButton="false" :width="800" :height="450">
                        <DxToolbarItem :template="customTitleTemplate" />
                        <DxToolbarItem toolbar="bottom" widget="dxButton" location="after"
                            :options="successButtonOptions" />
                        <DxToolbarItem toolbar="bottom" widget="dxButton" location="after"
                            :options="dangerButtonOptions" />
                    </DxPopup>

                    <DxFormEdit :col-count="2">
                        <DxItem data-field="ProdusId" :is-required="true">
                            <DxLabel text="Produs" />
                        </DxItem>
                        <DxItem data-field="Cantitate" :is-required="true" />
                        <DxItem data-field="UnitateId" :is-required="true">
                            <DxLabel text="Unitate" />
                        </DxItem>
                        <DxItem data-field="Tva" :is-required="true">
                            <DxLabel text="Procent TVA" />
                        </DxItem>
                        <DxItem data-field="PretUnitar" :is-required="true" />
                        <DxItem data-field="CategorieContabilaId" :is-required="true">
                            <DxLabel text="Categorie Contabilă" />
                        </DxItem>
                        <DxItem data-field="PretTotal" :read-only="true" />
                        <DxItem data-field="Total" :read-only="true" />
                    </DxFormEdit>

                    <DxTexts confirm-delete-title="Confirmare Ștergere"
                        :confirm-delete-message="deleteConfirmationMessage" />
                </DxEditing>

                <template #produsEditTemplate="{ data }">
                    <DxLookup :data-source="dsProdus" value-expr="Id" :display-expr="produsDisplayExpr"
                        :show-clear-button="true" :search-enabled="true" :value="data.value"
                        @value-changed="(e) => data.setValue(e.value)">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>

                <template #cantitateEditTemplate="{ data }">
                    <DxNumberBox :value="data.value" :min="1" :step="1" :show-spin-buttons="true"
                        :show-clear-button="true" label="Cantitate" @value-changed="(e) => data.setValue(e.value)" />
                </template>

                <template #unitateEditTemplate="{ data }">
                    <DxLookup :data-source="dsUnitate" value-expr="Id" display-expr="Nume" :show-clear-button="true"
                        :search-enabled="true" :value="data.value" @value-changed="(e) => data.setValue(e.value)">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>

                <template #tvaEditTemplate="{ data }">
                    <DxNumberBox :value="data.value" :min="0" :step="1" :show-spin-buttons="true"
                        :show-clear-button="true" format="## %" @value-changed="(e) => data.setValue(e.value)" />
                </template>

                <template #pretUnitarEditTemplate="{ data }">
                    <DxNumberBox :value="data.value" :min="0" :step="1" :show-spin-buttons="true"
                        :show-clear-button="true" :format="{ type: 'fixedPoint', precision: 4 }"
                        @value-changed="(e) => data.setValue(e.value)" />
                </template>

                <template #categorieContabilaEditTemplate="{ data }">
                    <DxLookup :data-source="dsCategorieContabila" value-expr="Id" display-expr="Nume"
                        :show-clear-button="true" :search-enabled="true" :value="data.value"
                        @value-changed="(e) => data.setValue(e.value)">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>

                <template #pretTotalEditTemplate="{ data }">
                    <DxNumberBox :value="data.value" :read-only="true" />
                </template>

                <template #totalEditTemplate="{ data }">
                    <DxNumberBox :value="data.value" :read-only="true" />
                </template>
                <DxGroupPanel :visible='true' emptyPanelText='Trage aici o coloana pentru a grupa datele' />
                <DxLoadPanel :enabled='true' />
                <DxSorting mode='multiple' />
                <DxColumnChooser :enabled='true' title='Selector coloane' :allowSearch="true" sortOrder="asc">
                </DxColumnChooser>
                <DxSearchPanel :visible='true' :width='240' placeholder='Cauta ...'></DxSearchPanel>
                <DxPaging :page-size='10' :page-index='1' />
                <DxToolbar>
                    <DxItem widget="dxButton" location="after" :options="addButtonOptions" />
                    <!-- <DxItem widget="dxButton" location="after" :options="editButtonOptions" /> -->
                    <DxItem name="searchPanel" />
                    <DxItem name="exportButton" />
                    <DxItem name="columnChooserButton" />
                </DxToolbar>

                <DxColumn caption="Produs">
                    <DxColumn data-field="ProdusId" :visible="false" :set-cell-value="onProdusIdCellValueChanged"
                        edit-cell-template="produsEditTemplate">
                        <DxLookup :data-source="dsProdus" value-expr="Id" :display-expr="produsDisplayExpr" />
                    </DxColumn>
                    <DxColumn data-field="Produs.Cod" caption="Cod Produs" :allowFiltering="true" />
                    <DxColumn data-field="Produs.Nume" caption="Nume Produs" :allowFiltering="true" />
                </DxColumn>
                <DxColumn caption="Unitate">
                    <DxColumn data-field="UnitateId" :visible="false" edit-cell-template="unitateEditTemplate">
                        <DxLookup :data-source="dsUnitate" value-expr="Id" display-expr="Nume" />
                    </DxColumn>
                    <DxColumn data-field="Unitate.Simbol" caption="Simbol Unitate" :allowFiltering="false" />
                    <DxColumn data-field="Unitate.Cod" caption="Cod Unitate" :allowFiltering="false" />
                    <DxColumn data-field="Unitate.Nume" caption="Nume Unitate" :allowFiltering="true" />
                </DxColumn>
                <DxColumn data-field="Nume" caption="Nume" :visible="false" :allowFiltering="false" />
                <DxColumn data-field="Custom" caption="Custom" :visible="false" :allowFiltering="false" />
                <DxColumn data-field="Cantitate" caption="Cantitate" data-type="number" :allowFiltering="false"
                    edit-cell-template="cantitateEditTemplate" />
                <DxColumn caption="Rezumat Financiar">
                    <DxColumn data-field="TVAUnitar" caption="TVA Unitar" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="ValoareTva" caption="Valoare TVA" data-type="number" format="#0.00"
                        :allowFiltering="false" />
                    <DxColumn data-field="Tva" caption="TVA" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" edit-cell-template="tvaEditTemplate" />
                    <DxColumn data-field="CategTva" caption="Categorie TVA" :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="CategTvaMotivScutire" caption="Categorie TVA Motiv Scutire" :visible="false"
                        :allowFiltering="false" />
                    <DxColumn data-field="PretUnitar" caption="Pret Unitar" data-type="number" format="#0.00"
                        :allowFiltering="false" edit-cell-template="pretUnitarEditTemplate" />
                    <DxColumn data-field="PretTotal" caption="Pret Total" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" edit-cell-template="pretTotalEditTemplate" />
                    <DxColumn data-field="CategorieContabilaId" :visible="false"
                        edit-cell-template="categorieContabilaEditTemplate">
                        <DxLookup :data-source="dsCategorieContabila" value-expr="Id" display-expr="Nume" />
                    </DxColumn>
                    <DxColumn data-field="Valoare" caption="Valoare" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" />
                    <DxColumn data-field="Total" caption="Total" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" edit-cell-template="totalEditTemplate" />
                    <DxColumn data-field="Pret" caption="Pret" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" />
                    <DxColumn data-field="ValoarePret" caption="Valoare Pret" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="AdaosUnitar" caption="Adaos Unitar" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="ValoareAdaos" caption="Valoare Adaos" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="Adaos" caption="Adaos" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" />
                    <DxColumn data-field="Brut" caption="Brut" data-type="number" format="#0.00" :visible="false"
                        :allowFiltering="false" />
                    <DxColumn data-field="PretUnitarValuta" caption="Pret Unitar Valuta" data-type="number"
                        format="#0.00" :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="PretCuAdaos" caption="Pret cu Adaos" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="ValoareCuAdaos" caption="Valoare cu Adaos" data-type="number" format="#0.00"
                        :allowFiltering="false" />
                    <DxColumn data-field="SumaDistribuita" caption="Suma Distribuita" data-type="number" format="#0.00"
                        :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="PretVanzareAdaos" caption="Pret Vanzare Adaos" data-type="number"
                        format="#0.00" :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="PretVanzareReferinta" caption="Pret Vanzare Referinta" data-type="number"
                        format="#0.00" :visible="false" :allowFiltering="false" />
                    <DxColumn data-field="PretVanzareAdaosProc" caption="Pret Vanzare Adaos Proc" data-type="number"
                        format="#0.00" :visible="false" :allowFiltering="false" />
                </DxColumn>

                <!-- Document-level Summary Columns (Mirroring DocumenteView, hidden by default) -->
                <DxColumn caption="Document Totals (Info)" :visible="false">
                    <DxColumn :calculate-cell-value="() => document.Valoare" caption="Valoare (Doc)"
                        :allowFiltering="false" format="#,##0.00" />
                    <DxColumn :calculate-cell-value="() => document.TVA" caption="TVA (Doc)" :allowFiltering="false"
                        format="#0" />
                    <DxColumn :calculate-cell-value="() => document.ValoareTVA" caption="Valoare TVA (Doc)"
                        :allowFiltering="false" format="#,##0.00" />
                    <DxColumn :calculate-cell-value="() => document.Total" caption="Total (Doc)" :allowFiltering="false"
                        format="#,##0.00" />
                    <DxColumn :calculate-cell-value="() => document.Sold" caption="Sold (Doc)" :allowFiltering="false"
                        format="#,##0.00" />
                    <DxColumn :calculate-cell-value="() => document.Valuta" caption="Valuta (Doc)"
                        :allowFiltering="false" />
                    <DxColumn :calculate-cell-value="() => document.Curs" caption="Curs (Doc)" :allowFiltering="false"
                        format="#,##0.0000" />
                    <DxColumn :calculate-cell-value="() => document.Adaos" caption="Adaos (Doc)" :allowFiltering="false"
                        format="#,##0.00" />
                </DxColumn>
                <DxColumn data-field="Intrari" caption="Intrari" data-type="number" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="Iesiri" caption="Iesiri" data-type="number" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="DataExp" caption="Data Expirarii" :allowFiltering="false" data-type="date"
                    format="yyyy-MM-dd" :visible="false" />
                <DxColumn data-field="LotData" caption="Data Lot" :allowFiltering="false" data-type="date"
                    format="yyyy-MM-dd" :visible="false" />
                <DxColumn data-field="CantitateFa" caption="CantitateFa" data-type="number" format="#0.00"
                    :visible="false" :allowFiltering="false" />
                <DxColumn data-field="CantitatePt1Fa" caption="CantitatePt1Fa" data-type="number" format="#0.00"
                    :visible="false" :allowFiltering="false" />
                <DxColumn data-field="CodLotIntern" caption="Cod Lot Intern" :visible="false" :allowFiltering="false" />
                <DxColumn data-field="CodLotFurnizor" caption="Cod Lot Furnizor" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="Sursa Finantare" caption="SursaFinantare" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="LocDepozitare" caption="Loc Depozitare" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="GarantieLuni" caption="Luni de Garantie" data-type="number" :visible="false"
                    :allowFiltering="false" />
                <DxColumn data-field="LotFixat" caption="Lot Fixat?" :visible="false" :allowFiltering="false"
                    cellTemplate="circleTemplate" />
                <DxColumn data-field="CodNc8" caption="Cod NC8" :visible="false" />
                <DxColumn data-field="MasaNet" caption="Masa Net" data-type="number" format="#0.00" :visible="false" />
                <DxColumn data-field="MasaBrut" caption="Masa Brut" data-type="number" format="#0.00"
                    :visible="false" />
                <DxColumn caption="Categorie" :visible="false">
                    <DxColumn data-field="Categorie.Cod" caption="Cod Categorie" :allowFiltering="false" />
                    <DxColumn data-field="Categorie.Nume" caption="Nume Categorie" :allowFiltering="false" />
                </DxColumn>
                <DxColumn type="buttons" :visible-index="9999" width="90" :buttons="['edit']" />
                <!-- <DxColumn v-if="isReadonly" caption="Actiuni" cell-template="deleteTemplate" :visible="true" /> -->

                <!-- <template #deleteTemplate="{ data }">
                    <DxButton v-if="!isReadonly" icon="edit" type="success" styling-mode="contained"
                        @click="editClicked(data.data)" />
                    <DxButton v-if="!isReadonly" icon="trash" type="danger" styling-mode="contained"
                        @click="deletePopupVisible = true" />
                </template> -->
                <template #circleTemplate="{ data }">
                    <div class="circle-container">
                        <div v-if="data.value" class="circle circle-true">✔</div>
                        <div v-else class="circle circle-false"></div>
                    </div>
                </template>
            </DxDataGrid>
        </div>

        <!-- Empty / Null States -->
        <div v-else-if="document" class="no-lines-empty-state">
            <i class="dx-icon-info"></i>
            <p>Acest document nu are linii adăugate.</p>
            <DxButton icon="add" text="Adaugă prima linie" type="success" styling-mode="contained"
                @click="onAddClick" />
        </div>
        <div v-if="document === null" class="null-document-state">
            <i class="dx-icon-warning"></i>
            <p>Vă rugăm să selectați un document pentru a vedea detaliile.</p>
        </div>

        <!-- Popups moved outside for accessibility in all states -->
        <DxPopupAdd v-model:visible="isModalAddVisible" :showTitle="true" :width="700" :height="450">
            <template #title>
                <div class="popup-header-linie">
                    <div class="popup-header-main-linie">
                        <span class="popup-header-icon-linie dx-icon-add"></span>
                        <div class="popup-header-texts-linie">
                            <div class="popup-header-title-linie">Adăugare linie document</div>
                            <div class="popup-header-subtitle-linie">
                                Completează detaliile liniei documentului în pașii de mai jos
                            </div>
                        </div>
                    </div>
                    <div class="popup-header-meta-linie">
                        <span class="status-chip-linie status-chip-linie--draft">
                            Nou
                        </span>
                    </div>
                </div>
            </template>
            <DxForm :form-data="linieNoua" :col-count="2">
                <DxSimpleItem data-field="ProdusId" template="produs" :isRequired="true">
                    <DxLabel text="Produs" />
                </DxSimpleItem>
                <DxSimpleItem data-field="Cantitate" template="cantitate" :isRequired="true" />
                <DxSimpleItem data-field="UnitateId" template="unitate" :isRequired="true">
                    <DxLabel text="Unitate" />
                </DxSimpleItem>
                <DxSimpleItem data-field="Tva" template="procentTVA" :isRequired="true">
                    <DxLabel text="Procent TVA" />
                </DxSimpleItem>
                <DxSimpleItem data-field="PretUnitar" template="pret-unitar" :isRequired="true" />
                <DxSimpleItem data-field="CategorieContabilaId" template="categorie-contabila" :isRequired="true">
                    <DxLabel text="Categorie Contabilă" />
                </DxSimpleItem>
                <DxSimpleItem data-field="PretTotal" template="pret-total" :readOnly="true" />
                <DxSimpleItem data-field="Total" template="total" :readOnly="true" />
                <template #produs>
                    <DxLookup :data-source="dsProdus" value-expr="Id" :display-expr="produsDisplayExpr"
                        :showClearButton="true" :searchEnabled="true" @value-changed="onValueChangedProdus"
                        :value="linieNoua.ProdusId">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>
                <template #cantitate>
                    <div>
                        <DxNumberBox :value="linieNoua.Cantitate" :min="1" :step="1" :show-spin-buttons="true"
                            :show-clear-button="true" label="Cantitate" @value-changed="onCantitateChanged" />
                    </div>
                </template>
                <template #unitate>
                    <DxLookup :data-source="dsUnitate" value-expr="Id" display-expr="Nume" :showClearButton="true"
                        :searchEnabled="true" :value="linieNoua.UnitateId" @value-changed="onUnitateChanged">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>
                <template #procentTVA>
                    <DxNumberBox :value="linieNoua.Tva" :min="0" :step="1" :show-spin-buttons="true"
                        :show-clear-button="true" format="## '%'" @value-changed="onTVAChanged" />
                </template>
                <template #pret-unitar>
                    <DxNumberBox :min="0" :value="linieNoua.PretUnitar" :step="1" :show-spin-buttons="true"
                        :show-clear-button="true" :format="{ type: 'fixedPoint', precision: 4 }"
                        @value-changed="onPretUnitarChanged" />
                </template>
                <template #categorie-contabila>
                    <DxLookup :data-source="dsCategorieContabila" value-expr="Id" display-expr="Nume"
                        :showClearButton="true" :searchEnabled="true" :value="linieNoua.CategorieContabilaId"
                        @value-changed="onCategorieContabilaChanged">
                        <DxDropDownOptions :show-title="false" />
                    </DxLookup>
                </template>
                <template #pret-total>
                    <DxNumberBox :value="linieNoua.PretTotal" :readOnly="true" />
                </template>
                <template #total>
                    <DxNumberBox :value="linieNoua.Total" :readOnly="true" />
                </template>
            </DxForm>
            <div class="form-footer-linie footer-fixed-linie footer-modern-linie">
                <div class="footer-right-linie">
                    <DxButton text="Salvează" type="success" width="120" height="32" styling-mode="outlined"
                        @click="onSaveAdd" />
                    <DxButton text="Închide" type="danger" width="120" height="32" styling-mode="outlined"
                        @click="onCloseAdd" />
                </div>
            </div>
        </DxPopupAdd>

    </div>
</template>
<script lang="ts">
import { locale, loadMessages } from 'devextreme/localization';
import {
    DxToolbar,
    DxToolbarItem,
    DxColumn,
    DxColumnChooser,
    DxDataGrid,
    DxEditing,
    DxExport,
    DxFilterRow,
    DxGrouping,
    DxGroupPanel,
    DxHeaderFilter,
    DxLoadPanel,
    DxPager,
    DxPaging,
    DxRemoteOperations,
    DxScrolling,
    DxSearchPanel,
    DxSelection,
    DxSorting,
    DxStateStoring,
    DxTexts,
    DxItem,
    DxPopup,
    DxForm as DxFormEdit,
} from 'devextreme-vue/data-grid';
import DxDateBox from 'devextreme-vue/date-box';
import DxPopupAdd from 'devextreme-vue/popup'
import { DxButton } from 'devextreme-vue/button';
import { DxCheckBox, DxScrollView } from 'devextreme-vue';
import ODataStore from 'devextreme/data/odata/store';
import DataSource from "devextreme/data/data_source";
import AppSetting from '@/settings/appSettings';
import { DxLookup, DxDropDownOptions } from 'devextreme-vue/lookup';
import { DxForm, DxSimpleItem, DxGroupItem, DxLabel, DxEmptyItem } from "devextreme-vue/form"
import DxTextArea from 'devextreme-vue/text-area'
import DxTextBox from 'devextreme-vue/text-box';
import { readonly, version } from 'vue';
import { DxNumberBox } from "devextreme-vue/number-box"
import notify from 'devextreme/ui/notify'
import DocumenteTipuriDataService from '@/services/Documente/DOC_Tipuri'
import { defineComponent, ref } from 'vue';
import DOC_Linii from '@/services/Documente/DOC_Linii';
import DOC_Documente from '@/services/Documente/DOC_Documente';
export default defineComponent({
    name: 'LiniView',
    components: {
        DxButton,
        DxToolbar,
        DxToolbarItem,
        DxScrollView,
        DxColumn,
        DxColumnChooser,
        DxDataGrid,
        DxEditing,
        DxExport,
        DxFilterRow,
        DxGrouping,
        DxGroupPanel,
        DxHeaderFilter,
        DxLoadPanel,
        DxLookup,
        DxPager,
        DxPaging,
        DxRemoteOperations,
        DxScrolling,
        DxSearchPanel,
        DxSelection,
        DxSorting,
        DxStateStoring,
        DxTexts,
        DxForm,
        DxSimpleItem,
        DxTextArea,
        DxGroupItem,
        DxCheckBox,
        DxLabel,
        DxTextBox,
        DxEmptyItem,
        DxDropDownOptions,
        DxDateBox,
        DxItem,
        DxPopupAdd,
        DxNumberBox,
        DxPopup,
        DxFormEdit
    },
    props: {
        document: Object,
    },
    computed: {
        successButtonOptions(): any {
            return {
                type: 'success',
                stylingMode: 'outlined',
                text: 'Salvează',
                onClick: () => {
                    this.$refs.dataGrid?.instance?.saveEditData();
                },
            };
        },
        dangerButtonOptions(): any {
            return {
                type: 'danger',
                stylingMode: 'outlined',
                text: 'Închide',
                onClick: () => {
                    this.onCancel();
                },
            };
        },
        customTitleTemplate() {
            return `
          <div class="popup-header" style="width: 800px; height: 60px; margin: -5px -20px 0 -20px; padding: 16px 24px; background: linear-gradient(90deg, #ff2c21, #ff6a4a); display: flex; align-items: center; gap: 16px;">
             <span class="dx-icon-edit" style="font-size: 24px; width: 30px; height: 40px; display: flex; align-items: center; justify-content: center; border-radius: 8px; color: white;"></span>
             <div style="display: flex; flex-direction: column; color: white;">
                <span style="font-size: 16px; font-weight: 700;">Editare Linie existentă</span>
                <span style="font-size: 12px; opacity: 0.9;">Modifică detaliile liniei selectate</span>
             </div>
          </div>
        `;
        },
        // editButtonOptions(): any {
        //     return {
        //         icon: this.isReadonly ? "edit" : "check",
        //         text: this.isReadonly ? "Permite modificarea pe linii" : "Finalizează",
        //         type: this.isReadonly ? "default" : "success",
        //         stylingMode: "outlined",
        //         onClick: () => this.updateReadOnly()
        //     };
        // },
    },
    data() {
        return {
            deleteConfirmationMessage: "Sigur doriți să ștergeți această înregistrare?",
            dsLinii: {} as ODataStore,
            dsProduse: {} as ODataStore,
            editingOriginalRow: null as any,
            isStornatRetur: false,
            isReadonly: true,
            isDisabled: true,
            isModalAddVisible: false,
            deletePopupVisible: false,
            partenerNou: {
                ID: null,
                Nume: this.document?.Partener,
                Cod: this.document?.PartenerCod,
                CUI: this.document?.PartenerCUI,
                GrupPartener: this.document?.GrupPartener
            },
            addButtonOptions: {
                icon: "add",
                type: "success",
                stylingMode: "contained",
                onClick: () => this.onAddClick()
            },
            categorieDocument: null,
            linieNoua: {
                DocumentId: this.document?.Id,
                Categorie: this.document?.Categorie,
                ProdusId: null,
                UnitateId: null,
                Nume: '',
                Cantitate: null,
                Tvaunitar: null,
                ProcentTVA: null,
                PretUnitar: 0,
                Valoare: null,
                ValoareTva: null,
                Total: 0,
                Tva: this.document?.TVA ?? this.document?.Tva ?? 0,
                Pret: null,
                ValoarePret: null,
                AdaosUnitar: null,
                ValoareAdaos: 0, //to-do
                Adaos: null,
                Brut: null,
                PretCuAdaos: null,
                ValoareCuAdaos: null,
                PretVanzareAdaos: null,
                PretVanzareReferinta: null,
                PretVanzareAdaosProc: null,
                CategorieContabilaId: null,
                PretTotal: 0,

            },
            tipDocument: null,

            dsCategorieDocumente: null,
            dsTipDocumente: null,
            dsAgent: null,
            dsZone: null,
            dsNumereContracte: null,
            dsPartener: null,
            dsGrupePartener: null,
            dsComenzi: null,
            dsProiecte: null,
            dsStructuriIdEditabile: null,
            dsGestiune: null,
            dsValute: null,
            dsProdus: null,
            dsUnitate: null,
            dsCategorieContabila: null,
            areLinii: false
        }
    },
    methods: {
        onEditingStartLinie(e: any) {
            this.editingOriginalRow = JSON.parse(JSON.stringify(e.data ?? {}));
        },
        onGridSaved() {
            this.$emit('refresh-document', this.document.Id);
        },
        setDeleteConfirmationMessage() {
            const grid = this.$refs.dataGrid?.instance;
            if (grid) {
                // Folosește string pentru a accesa adânc în obiect fără a-l suprascrie
                grid.option('editing.texts.confirmDeleteMessage', this.deleteConfirmationMessage);
                grid.option('editing.texts.confirmDeleteTitle', "Confirmare Ștergere");
            }
        },
        onCancel() {
            this.isModalAddVisible = false;
            this.$refs.dataGrid?.instance?.cancelEditData();
        },
        getDataSource(cellData: any) {
            // Verificăm dacă structura există înainte de a accesa
            return cellData?.editorOptions?.dataSource;
        },
        async shareClicked() {
            const textToCopy = `${window.location.origin}` + `/documente?id=${this.document.Id}`

            if (!textToCopy) {
                notify('Nu există ID de document de copiat.', 'warning', 5000);
                return;
            }

            try {
                await navigator.clipboard.writeText(textToCopy);
                notify('Adresa URL a fost copiată', 'success', 5000);
            } catch (err) {
                console.error('Eroare la copiere:', err);
                notify('Eroare la copiere. Copiere manuală necesară.', 'error', 5000);
            }
        },
        onCantitateChanged(e: any) {
            this.linieNoua.Cantitate = e.value
            this.calculateTotalsLinie()
        },
        onPretUnitarChanged(e: any) {
            this.linieNoua.PretUnitar = e.value
            this.calculateTotalsLinie()
        },
        onTVAChanged(e: any) {
            this.linieNoua.Tva = e.value
            this.calculateTotalsLinie()
        },
        calculateTotalsLinie() {
            if (this.linieNoua.PretUnitar !== null && this.linieNoua.Cantitate !== null) {
                this.linieNoua.ValoarePret = this.linieNoua.PretUnitar * this.linieNoua.Cantitate
                this.linieNoua.Valoare = this.linieNoua.ValoarePret
                this.linieNoua.ValoareCuAdaos = this.linieNoua.ValoarePret
                this.linieNoua.Pret = this.linieNoua.PretUnitar

                DocumenteTipuriDataService.getByKey(this.document.TipId).then((tip) => {
                    this.tipDocument = tip

                    if (this.tipDocument.data.AreAdaos === true) {
                        if (this.linieNoua.Cantitate !== 0)
                            this.linieNoua.AdaosUnitar = this.linieNoua.ValoareAdaos / this.linieNoua.Cantitate
                        this.linieNoua.ValoareCuAdaos = this.linieNoua.Valoare + (this.linieNoua.ValoareAdaos === null ? 0 : this.linieNoua.ValoareAdaos)
                    } else {
                        this.linieNoua.Adaos = 0
                        this.linieNoua.AdaosUnitar = 0
                        this.linieNoua.ValoareAdaos = 0
                    }

                    if (this.linieNoua.Cantitate !== null && this.linieNoua.Cantitate !== 0) {
                        this.linieNoua.PretCuAdaos = this.linieNoua.ValoareCuAdaos / this.linieNoua.Cantitate
                    } else {
                        this.linieNoua.PretCuAdaos = this.linieNoua.PretUnitar + this.linieNoua.AdaosUnitar
                    }

                    if (this.tipDocument.data.AreTva === true) {
                        if (this.tipDocument.data.Tvaaplicat === true) {
                            this.linieNoua.Tvaunitar = this.linieNoua.PretCuAdaos * ((this.linieNoua.Tva === null ? 0 : this.linieNoua.Tva) / 100)
                            this.linieNoua.PretTotal = this.linieNoua.PretCuAdaos + this.linieNoua.Tvaunitar
                            this.linieNoua.ValoareTva = this.linieNoua.ValoareCuAdaos * (this.linieNoua.Tva === null ? 0 : this.linieNoua.Tva) / 100
                            this.linieNoua.Total = this.linieNoua.ValoareCuAdaos + this.linieNoua.ValoareTva
                        } else {
                            this.linieNoua.Total = this.linieNoua.ValoareCuAdaos
                            this.linieNoua.ValoareCuAdaos = this.linieNoua.Total / (1 + (this.linieNoua.Tva === null ? 0 : this.linieNoua.Tva) / 100)
                            this.linieNoua.ValoareTva = this.linieNoua.Total - this.linieNoua.ValoareCuAdaos
                            this.linieNoua.Valoare = this.linieNoua.Valoare - this.linieNoua.ValoareTva
                            this.linieNoua.Tvaunitar = this.linieNoua.Cantitate === null ? 0 : this.linieNoua.ValoareTva / this.linieNoua.Cantitate
                            this.linieNoua.PretTotal = this.linieNoua.PretCuAdaos
                        }
                    } else {
                        this.linieNoua.Tva = 0;
                        this.linieNoua.Tvaunitar = 0;
                        this.linieNoua.PretTotal = this.linieNoua.PretCuAdaos;
                        this.linieNoua.ValoareTva = 0;
                        this.linieNoua.Total = this.linieNoua.ValoareCuAdaos;
                    }

                    if (this.linieNoua.Brut === null)
                        this.linieNoua.Brut = 0
                    else {
                        if (this.linieNoua.PretCuAdaos !== 0) {
                            this.linieNoua.PretVanzareAdaosProc = ((this.linieNoua.Brut / (this.linieNoua.PretCuAdaos * (1 + this.linieNoua.Tva / 100))) * 100) - 100
                            this.linieNoua.PretVanzareAdaos = (this.linieNoua.Brut - (this.linieNoua.PretCuAdaos * (1 + this.linieNoua.Tva / 100))) / (1 + this.linieNoua.Tva / 100)
                        }
                    }
                }).catch(error => {
                    console.error("Eroare la aducerea tipului de document: ", error)
                })
            }

        },
        produsDisplayExpr(item: any) {
            if (!item) return "";
            return `${item.Cod || ''} - ${item.Nume || ''} - ${item.CodIntern || ''}`;
        },
        goBack() {
            this.$router.push('/documente')
            this.$emit('back')
        },
        updateReadOnly() {
            if (this.isReadonly === true) {
                this.isReadonly = false
                this.isDisabled = false
            }
            else {
                this.isReadonly = true
                this.isDisabled = true
            }
        },
        // onValueChangedCategorie(e: any) {
        //     let _filter;
        //     if (e.value != null) {
        //         _filter = ["CategorieId", "=", e.value];
        //     } else {
        //         _filter = null;
        //     }
        //     this.dsTipDocumente.filter(_filter);
        //     this.dsTipDocumente.load();
        // },
        onTipDocumentSelected(e: any) {
            this.document.TipId = e.value
            this.dsTipDocumente.store().byKey(this.document.TipId).then((tip) => {
                this.dsCategorieDocumente.store().byKey(tip.CategorieId).then((categorie) => {
                    this.categorieDocument = categorie
                })
            })
        },
        onPartenerSelected(e: any) {
            this.partenerNou.ID = e.selectedItem.ID
            this.partenerNou.Nume = e.selectedItem.Nume
            this.partenerNou.CUI = e.selectedItem.CUI
            this.partenerNou.Cod = e.selectedItem.Cod

            this.dsGrupePartener.filter("Id", "=", e.selectedItem.GrupID)
            this.dsGrupePartener.load().then((data: any[]) => {
                if (data && data.length > 0)
                    this.partenerNou.GrupPartener = data[0].Nume
            })
        },
        onPartenerCUISelected(e: any) {
            this.partenerNou.ID = e.selectedItem.ID
            this.partenerNou.Nume = e.selectedItem.Nume
            this.partenerNou.CUI = e.selectedItem.CUI
            this.partenerNou.Cod = e.selectedItem.Cod

            this.dsGrupePartener.filter("Id", "=", e.selectedItem.GrupID)
            this.dsGrupePartener.load().then((data: any[]) => {
                if (data && data.length > 0)
                    this.partenerNou.GrupPartener = data[0].Nume
            })
        },
        onAddClick() {
            this.isModalAddVisible = true
        },
        onCloseAdd() {
            this.isModalAddVisible = false
            this.resetLinieNoua()
        },
        resetLinieNoua() {
            Object.assign(this.linieNoua, {
                DocumentId: this.document?.Id,
                ProdusId: null,
                UnitateId: null,
                Nume: '',
                Cantitate: null,
                Tvaunitar: null,
                ProcentTVA: null,
                PretUnitar: 0,
                Valoare: null,
                ValoareTva: null,
                Total: 0,
                Tva: this.document?.TVA ?? this.document?.Tva ?? 0,
                Pret: null,
                ValoarePret: null,
                AdaosUnitar: null,
                ValoareAdaos: 0,
                Adaos: null,
                Brut: null,
                PretCuAdaos: null,
                ValoareCuAdaos: null,
                PretVanzareAdaos: null,
                PretVanzareReferinta: null,
                PretVanzareAdaosProc: null,
                CategorieContabilaId: null,
                PretTotal: 0,
            });
        },
        async onSaveAdd() {
            if (!this.linieNoua.ProdusId || !this.linieNoua.Cantitate || this.linieNoua.Cantitate <= 0) {
                notify("Selectează produsul și cantitatea.", "error", 3000);
                return;
            }
            if (!this.linieNoua.DocumentId) {
                notify("Lipsește DocumentId.", "error", 3000);
                return;
            }

            // payload curat (NU include Categorie/Produs/Unitate ca texte sau obiecte)
            const payload = {
                ProdusId: this.linieNoua.ProdusId ?? null,
                UnitateId: this.linieNoua.UnitateId ?? null,
                Cantitate: this.linieNoua.Cantitate ?? null,
                TvaLinie: this.linieNoua.Tva ?? null,
                PretUnitar: this.linieNoua.PretUnitar ?? this.linieNoua.Pret ?? null,
                CategorieContabilaId: this.linieNoua.CategorieContabilaId ?? null
            };

            console.log("POST payload (clean):", payload);

            try {
                await DOC_Linii.save(this.linieNoua.DocumentId, payload);
                notify("Linia a fost adăugată cu succes.", "success", 2000);
                this.$emit('refresh-document', this.document.Id);
                this.onCloseAdd();

                // Reload data source and update visibility
                const items = await this.dsLinii.reload();
                this.areLinii = items && items.length > 0;

                // If grid exists, force refresh
                if (this.$refs.dataGrid) {
                    (this.$refs.dataGrid as any).instance.refresh();
                }
            } catch (error) {
                console.error("Eroare la salvarea liniei:", error);
                const msg =
                    error?.response?.data?.error?.message ||
                    error?.response?.data?.message ||
                    error?.response?.data?.title ||
                    error?.message ||
                    "A apărut o eroare la salvare.";
                notify(msg, "error", 4000);
            }
        },
        async onEditLinie() {
            // Deprecated: Grid built-in editing is used instead
        },
        EditDocumentClicked() {
            this.isReadonly = false;
        },
        async SaveDocumentClicked() {
            try {
                if (!this.document?.Id) {
                    notify("Lipsește Id-ul documentului.", "error", 3000);
                    return;
                }

                // Payload curat: DOAR proprietăți care există în DOC_Documente (EF)
                const payload = {
                    TipId: this.document.TipId ?? null,
                    Numar: this.document.Numar ?? null,
                    Data: this.document.Data ?? null,
                    DataDoc: this.document.DataDoc ?? null,
                    Scadenta: this.document.Scadenta ?? null,

                    GestiuneIntrareId: this.document.GestiuneIntrareId ?? null,
                    GestiuneIesireId: this.document.GestiuneIesireId ?? null,

                    Document: this.document.Document ?? null,
                    ValutaId: this.document.ValutaId ?? null,
                    Curs: this.document.Curs ?? this.document.PretCurs ?? null,
                    PretValutaId: this.document.PretValutaId ?? null,
                    PretCurs: this.document.PretCurs ?? this.document.Curs ?? null,

                    Tva: this.document.Tva ?? null,
                    Valoare: this.document.Valoare ?? null,
                    ValoareTva: this.document.ValoareTva ?? null,
                    Accize: this.document.Accize ?? null,
                    Total: this.document.Total ?? null,

                    Explicatie: this.document.Explicatie ?? null,
                    Observatii: this.document.Observatii ?? null,

                    PartenerId: this.document.PartenerId ?? null,
                    ContractNumar: this.document.ContractNumar ?? null,
                    ContractId: this.document.ContractId ?? null,

                    Adaos: this.document.Adaos ?? null,
                    AdaosValoare: this.document.AdaosValoare ?? null,

                    AreCaf: this.document.AreCaf ?? null,
                    ProcentCaf: this.document.ProcentCaf ?? null,
                    ValoareCaf: this.document.ValoareCaf ?? null,

                    AgentId: this.document.AgentId ?? null,
                    LocDepozitare: this.document.LocDepozitare ?? null,

                    StornatRetur: this.document.StornatRetur ?? null,
                    Autofacturat: this.document.Autofacturat ?? null,
                    EmisTert: this.document.EmisTert ?? null,
                    TvaLaIncasare: this.document.TvaLaIncasare ?? null,

                    ZonaId: this.document.ZonaId ?? null,

                    // dacă ai în UI:
                    ComandaId: this.document.ComandaId ?? null,
                    ProiectId: this.document.ProiectId ?? null,
                    StructIdEditabil: this.document.StructIdEditabil ?? null,
                    StructuraId: this.document.StructuraId ?? null,
                    NotaId: this.document.NotaId ?? null
                };

                console.log("PATCH payload (clean):", payload);

                await DOC_Documente.update(this.document.Id, payload);

                notify("Documentul a fost salvat cu succes.", "success", 2000);
                this.isReadonly = true;
            } catch (error) {
                console.error("Eroare la salvarea documentului:", error);
                const msg =
                    error?.response?.data?.error?.message ||
                    error?.response?.data?.message ||
                    error?.response?.data?.title ||
                    error?.message ||
                    "Eroare la salvare.";
                notify(msg, "error", 3000);
            }
        },
        onUnitateChanged(e: any) {
            this.linieNoua.UnitateId = e.value;
        },
        onCategorieContabilaChanged(e: any) {
            this.linieNoua.CategorieContabilaId = e.value;
        },
        onProdusIdCellValueChanged(newData: any, value: any) {
            newData.ProdusId = value;
            if (value) {
                return this.dsProdus.store().byKey(value).then((produs: any) => {
                    if (produs) {
                        newData.UnitateId = produs.UnitateId;
                        newData.CategorieContabilaId = produs.CategorieContabilaId;
                    }
                });
            }
        },
        onValueChangedProdus(e: any) {
            let _filter
            let categContID
            let unitateID
            if (e.value != null) {
                this.linieNoua.ProdusId = e.value
                this.dsProdus.store().byKey(e.value).then((produs) => {
                    categContID = produs ? produs.CategorieContabilaId : null
                    _filter = ["Id", "=", categContID]
                    this.dsCategorieContabila.filter(_filter)
                    this.dsCategorieContabila.load()

                    unitateID = produs ? produs.UnitateId : null

                    this.linieNoua.CategorieContabilaId = categContID
                    this.linieNoua.UnitateId = unitateID

                })
            }
            else {
                _filter = null
                this.linieNoua.ProdusId = null
                this.linieNoua.CategorieContabilaId = null
                this.linieNoua.UnitateId = null
            }

        },
        headerEditClicked() {
            this.isReadonly = false;
        },
        headerSaveClicked() {
            this.submitForm();
            this.isReadonly = true;
        },
        closeClicked() {
            this.goBack();
        },
        onValueChangedProdusEdit(e: any) {
            // Deprecated
        },
        loadLinii() {
            this.dsLinii = new DataSource({
                store: {
                    type: 'odata',
                    url: AppSetting.API_Url_DocumenteOdata + `DOC_Linii`,
                    key: 'Id',
                    keyType: "Guid",
                    version: 4,
                    fieldTypes: {
                        DocumentId: "Guid"
                    },
                    beforeSend: (e: any) => {
                        e.headers = {
                            "Content-Type": "application/json",
                            "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                        };
                        e.params.$expand = "Categorie($select=Cod,Nume),Produs($select=Cod,Nume),Unitate($select=Simbol,Nume,Cod)"
                    },
                },
            });
            this.dsLinii.filter(["DocumentId", "=", this.document.Id]);
            this.dsLinii.load().then((loadedData: any[]) => {
                this.areLinii = loadedData && loadedData.length > 0
            })
        },
        submitForm() {
            // Logic to save the whole document form
        },
        formatDate(date: any) {
            if (!date) return '';
            const d = new Date(date);
            return d.toLocaleDateString('ro-RO');
        },
        buildLiniePayload(row: any) {
            return {
                ProdusId: row?.ProdusId ?? null,
                UnitateId: row?.UnitateId ?? null,
                Cantitate: row?.Cantitate ?? null,
                TvaLinie: row?.Tva ?? null,
                PretUnitar: row?.PretUnitar ?? row?.Pret ?? null,
                CategorieContabilaId: row?.CategorieContabilaId ?? null
            };
        },
        onSavingLinie(e: any) {
            const change = e.changes?.[0];

            if (!change || change.type !== 'update') {
                return;
            }

            e.cancel = true;

            e.promise = (async () => {
                try {
                    const mergedRow = {
                        ...(this.editingOriginalRow ?? {}),
                        ...(change.data ?? {})
                    };

                    const payload = this.buildLiniePayload(mergedRow);

                    await DOC_Linii.update(change.key, payload);

                    await this.dsLinii.reload();

                    if (this.$refs.dataGrid) {
                        (this.$refs.dataGrid as any).instance.cancelEditData();
                        (this.$refs.dataGrid as any).instance.refresh();
                    }

                    this.areLinii = true;
                    this.$emit('refresh-document', this.document.Id);

                    notify("Linia a fost actualizată cu succes.", "success", 2000);
                } catch (error: any) {
                    console.error("Eroare la actualizarea liniei:", error);

                    const msg =
                        error?.response?.data?.error?.message ||
                        error?.response?.data?.message ||
                        error?.response?.data?.title ||
                        error?.message ||
                        "A apărut o eroare la actualizarea liniei.";

                    notify(msg, "error", 4000);
                    throw error;
                } finally {
                    this.editingOriginalRow = null
                }
            })();
        },
    },
    mounted() {

        if (this.document !== null) {
            this.loadLinii()
        }
        this.dsCategorieDocumente = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'DOC_Categorii',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsTipDocumente = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `DOC_Tipuri`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            }
        });
        this.dsTipDocumente.store().byKey(this.document.TipId).then((tip) => {
            this.dsCategorieDocumente.store().byKey(tip.CategorieId).then((categorie) => {
                this.categorieDocument = categorie
            })
        })
        this.dsAgent = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'COM_Agenti',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")

                    };
                }
            },
        });
        this.dsZone = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'ERP_Zone',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsNumereContracte = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_ComercialOdata + 'COM_Contracte',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
            select: [
                'Id',
                'NumarContract'
            ]
        });
        this.dsPartener = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'PAR_Parteneri',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    }
                }
            }
        });
        this.dsGrupePartener = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'PAR_Grupuri',
                key: 'ID',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsProiecte = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `DOC_Proiecte`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsStructuriIdEditabile = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'ERP_StructuraCompanie',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            }
        });
        this.dsGestiune = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `GES_Gestiuni`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsValute = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_ComercialOdata + `ERP_Valute`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsComenzi = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + `PRO_Comenzi`,
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.dsProdus = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'PRO_Produse',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    }
                }
            },
            select: [
                'Id',
                'Cod',
                'Nume',
                'UnitateId',
                'CategorieContabilaId',
                'CodIntern',
                'Descriere'
            ],
        });
        this.dsUnitate = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_ComercialOdata + 'PRO_Unitati',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    }
                }
            },
        });
        this.dsCategorieContabila = new DataSource({
            store: {
                type: 'odata',
                url: AppSetting.API_Url_DocumenteOdata + 'CON_CategoriiContabile',
                key: 'Id',
                keyType: "Guid",
                version: 4,
                beforeSend: (e: any) => {
                    e.headers = {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + localStorage.getItem("ERA_Jwt")
                    };
                }
            },
        });
        this.setDeleteConfirmationMessage();
        loadMessages({
            "ro": {
                "Yes": "Da",
                "No": "Nu",
            }
        });

        locale('ro');
    }
});
</script>
<style>
.footer-fixed-linie {
    position: sticky;
    bottom: 0;
    padding: 10px 16px;
    display: flex;
    /* Forțează conținutul spre dreapta */
    justify-content: flex-end;
    gap: 12px;
    z-index: 10;
    background-color: white;
    /* Recomandat pentru sticky footer */
}

.footer-modern-linie {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 12px 24px;
    box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.05);
}

.footer-right-linie {
    display: flex;
    gap: 12px;
    /* Spațiu între cele două butoane */
}

.popup-header-linie {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 18px;
    background: linear-gradient(90deg, #ff2c21, #ff6a4a);
    color: #fff;
}

.popup-header-main-linie {
    display: flex;
    align-items: center;
    gap: 10px;
}

.popup-header-icon-linie {
    font-size: 20px;
}

.popup-header-texts-linie {
    display: flex;
    flex-direction: column;
}

.popup-header-title-linie {
    font-size: 15px;
    font-weight: 700;
}

.popup-header-subtitle-linie {
    font-size: 12px;
    opacity: 0.85;
}

.popup-header-meta-linie {
    display: flex;
    align-items: center;
}

.status-chip-linie {
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    border: 1px solid rgba(255, 255, 255, 0.7);
}

.status-chip--draft-linie {
    background-color: rgba(255, 255, 255, 0.18);
}

.lini-view-container {
    padding: 12px 20px;
    background: #fdfdfd;
    min-height: 100%;
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    position: relative;
    overflow: hidden;
}

.lean-header-strip {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 64px;
    background: white;
    padding: 0 16px;
    border-radius: 12px;
    margin-bottom: 12px;
    border: 1px solid #edf2f7;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
}

.header-left {
    display: flex;
    align-items: center;
    gap: 16px;
    flex: 1;
}

.doc-identity {
    display: flex;
    align-items: center;
    gap: 12px;
}

.panel-actions {
    display: flex;
    align-items: center;
    gap: 8px;
    /* distanța dintre butoane */
}

.doc-identity i {
    font-size: 20px;
    color: #ff2c21;
    background: #fff5f5;
    padding: 10px;
    border-radius: 10px;
}

.doc-text {
    display: flex;
    flex-direction: column;
}

.partner-name {
    font-size: 14px;
    font-weight: 700;
    color: #1a202c;
    max-width: 200px;
}

.doc-meta-chips {
    display: flex;
    gap: 8px;
    margin-top: 4px;
    flex-wrap: wrap;
}

.chip {
    padding: 2px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.chip.no {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.chip.date {
    background: #f1f5f9;
    color: #64748b;
}

.chip.type {
    background: #fff1f2;
    color: #e11d48;
}

.chip.valoare {
    background: #f1f5f9;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.chip.tva {
    background: #f0fdf4;
    color: #166534;
    border: 1px solid #dcfce7;
}

.chip.tva-val {
    background: #fffbeb;
    color: #92400e;
    border: 1px solid #fef3c7;
}

.chip.total {
    background: #3f4249;
    color: white;
    border: 1px solid #2d3748;
}

.chip.sold {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fee2e2;
}

.chip.curs {
    background: #f5f3ff;
    color: #5b21b6;
    border: 1px solid #ede9fe;
}

.chip.adaos {
    background: #ecfeff;
    color: #155e75;
    border: 1px solid #cffafe;
}

.chip.no {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.chip.date {
    background: #f1f5f9;
    color: #64748b;
}

.chip.type {
    background: #fff1f2;
    color: #e11d48;
}

.header-center {
    flex: 0 0 auto;
    padding: 0 24px;
}

.total-badge {
    background: #3f4249;
    color: white;
    padding: 6px 16px;
    border-radius: 8px;
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.total-badge .label {
    font-size: 10px;
    font-weight: 500;
    color: #a0aec0;
}

.total-badge .amount {
    font-size: 18px;
    font-weight: 800;
}

.total-badge .curr {
    font-size: 12px;
    font-weight: 500;
    color: #cbd5e0;
}

.header-right {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    justify-content: flex-end;
}

.lean-tool-btn {
    border-radius: 8px !important;
    width: 36px !important;
    height: 36px !important;
}

.save-btn.active {
    background: #48bb78 !important;
    color: white !important;
    box-shadow: 0 2px 4px rgba(72, 187, 120, 0.3);
}

.v-divider {
    width: 1px;
    height: 20px;
    background: #e2e8f0;
    margin: 0 8px;
}

/* Grid & Empty State (Condensed) */
:deep(.dx-datagrid) {
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid #edf2f7;
}


.no-lines-empty-state,
.null-document-state {
    text-align: center;
    padding: 40px;
    background: white;
    border-radius: 12px;
    border: 1px dashed #e2e8f0;
    color: #718096;
    margin-top: 12px;
}

.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}


.dx-form .dx-form-group:has(.dx-form-group-caption) {
    border: 2px solid #ff6600 !important;
    border-radius: 8px !important;
    padding: 20px !important;
    margin-bottom: 25px !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05) !important;
    min-height: 320px;
    margin-left: 30px !important;
}

.dx-field-item {
    padding-top: 6px !important;
    padding-bottom: 0px !important;
    padding-left: 5px !important;
    padding-right: 0px !important;
    margin: 0px !important;
}

.dx-form-group-caption {
    color: #ff6600;
    font-weight: bold;
}

.buttons {
    margin-top: 40px;
}


.cantitate-numberbox {
    margin-left: 130px;
    margin-top: 23px;
}
</style>
